import React, { useState, useEffect, useRef, useCallback } from 'react';

const LOADING_STEPS = [
  { label: 'Analyzing investment personality', duration: 2500 },
  { label: 'Finding your market match', duration: 3000 },
  { label: 'Building custom portfolio', duration: 3500 },
  { label: 'Curating your recommendations', duration: 3000 },
];

const FAST_DURATION = 800;

interface YourTypeLoadingProps {
  dataReady: boolean;
  onComplete: () => void;
}

export default function YourTypeLoading({ dataReady, onComplete }: YourTypeLoadingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const dataReadyRef = useRef(dataReady);
  const hasCompletedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    dataReadyRef.current = dataReady;
  }, [dataReady]);

  const advanceStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= LOADING_STEPS.length) {
        // All steps done
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          // Small delay before triggering completion for final checkmark to appear
          setTimeout(() => onComplete(), 400);
        }
        return prev;
      }
      return next;
    });
    setStepProgress(0);
  }, [onComplete]);

  useEffect(() => {
    if (hasCompletedRef.current) return;

    const isLastStep = currentStep === LOADING_STEPS.length - 1;
    const isReady = dataReadyRef.current;

    // Determine duration for this step
    let duration: number;
    if (isReady && !isLastStep) {
      // Data ready early: fast-forward remaining steps
      duration = FAST_DURATION;
    } else if (isLastStep && !isReady) {
      // Last step but data not ready: don't auto-advance, keep spinning
      // We'll handle this via the dataReady effect below
      const progressInterval = setInterval(() => {
        setStepProgress((p) => Math.min(p + 0.3, 95)); // Creep up to 95% but never finish
      }, 50);
      return () => clearInterval(progressInterval);
    } else {
      duration = LOADING_STEPS[currentStep]?.duration ?? 3000;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setStepProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        advanceStep();
      }
    }, 30);

    return () => clearInterval(interval);
  }, [currentStep, dataReady, advanceStep]);

  // Handle: data becomes ready while stuck on last step
  useEffect(() => {
    if (
      dataReady &&
      currentStep === LOADING_STEPS.length - 1 &&
      !hasCompletedRef.current
    ) {
      // Quick finish the last step
      const startProgress = stepProgress;
      const remaining = 100 - startProgress;
      const finishDuration = 600;
      const startTime = Date.now();

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = startProgress + (elapsed / finishDuration) * remaining;
        setStepProgress(Math.min(progress, 100));

        if (progress >= 100) {
          clearInterval(interval);
          if (!hasCompletedRef.current) {
            hasCompletedRef.current = true;
            setTimeout(() => onComplete(), 400);
          }
        }
      }, 20);

      return () => clearInterval(interval);
    }
  }, [dataReady, currentStep, onComplete, stepProgress]);

  return (
    <div className="h-[60vh] flex flex-col items-center justify-center gap-8 animate-in fade-in duration-700">
      <h2 className="text-xl font-bold uppercase tracking-widest text-white">
        Analyzing your vibe
      </h2>

      <div className="flex flex-col gap-5 w-full max-w-sm">
        {LOADING_STEPS.map((step, idx) => {
          const isDone = idx < currentStep || (idx === currentStep && stepProgress >= 100);
          const isActive = idx === currentStep && stepProgress < 100;
          const isPending = idx > currentStep;

          return (
            <div
              key={idx}
              className={`flex items-center gap-3 transition-all duration-500 ${
                isPending ? 'opacity-30' : 'opacity-100'
              }`}
            >
              {/* Status icon */}
              <div className="flex-none size-7 flex items-center justify-center">
                {isDone ? (
                  <div className="size-7 rounded-full bg-teal-200/20 flex items-center justify-center animate-in zoom-in duration-300">
                    <svg className="size-4 text-teal-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : isActive ? (
                  <div className="size-6 rounded-full border-2 border-teal-200/20 border-t-teal-200 animate-spin" />
                ) : (
                  <div className="size-2 rounded-full bg-white/20" />
                )}
              </div>

              {/* Label */}
              <span
                className={`text-base transition-colors duration-300 ${
                  isDone
                    ? 'text-teal-200/70'
                    : isActive
                    ? 'text-white animate-pulse'
                    : 'text-white/30'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
